/**
 * Postal-code helpers shared across estimates, service-areas, and any other
 * surface that takes a US ZIP or Canadian postal code from the user.
 *
 * The estimates flow historically forced US-5-digit input (maxLength=5 +
 * strip-non-digits), which silently rejected Canadian postal codes. These
 * helpers centralise the format detection so every input behaves the same.
 */

/**
 * The capture group is the 5-DIGIT PREFIX, which is what `normalizePostalAreaCode`
 * needs. It used to wrap the `+4` suffix instead, so "60607-2031" resolved to the
 * area key "-2031" and matched no service area or tax zone at all.
 */
const US_ZIP_RE = /^(\d{5})(?:-\d{4})?$/;

/** Canadian postal code — 6 alphanumeric chars, optional separator space. */
const CA_POSTAL_RE = /^[A-Z]\d[A-Z]\d[A-Z]\d$/;

/** Canadian forward sortation area — first 3 chars of a postal code. */
const CA_FSA_RE = /^[A-Z]\d[A-Z]$/;

/**
 * Canadian province / territory two-letter codes — used by tax-zone rows to
 * distinguish CA zones from US zones (whose `stateCode` is also two letters).
 * Source: CanadianTaxService PROVINCE_TAX_CONFIG (operations).
 */
const CA_PROVINCE_CODES = new Set<string>([
  'AB',
  'BC',
  'MB',
  'NB',
  'NL',
  'NS',
  'NT',
  'NU',
  'ON',
  'PE',
  'QC',
  'SK',
  'YT',
]);

/** True if the two-letter code is a Canadian province / territory. */
export function isCaProvinceCode(value: string | null | undefined): boolean {
  if (!value) return false;
  return CA_PROVINCE_CODES.has(value.trim().toUpperCase());
}

/**
 * Max characters we ever accept in a postal-code input. Covers:
 *   - US ZIP+4 with hyphen: "12345-6789" → 10
 *   - Canadian postal with space: "K1A 0B1" → 7
 * Picking 10 is the upper bound; 7 would block US ZIP+4.
 */
export const POSTAL_CODE_MAX_LENGTH = 10;

/** Strip whitespace, uppercase. Keeps the user's raw chars for re-render. */
export function normalizePostalCode(value: string): string {
  return value.trim().toUpperCase().replace(/\s+/g, '');
}

/**
 * Normalize to the key used for service areas/tax zones. A US ZIP+4 reduces to
 * its 5-digit prefix — service areas and tax zones are keyed on that, never on
 * the delivery-route suffix.
 */
export function normalizePostalAreaCode(value: string): string {
  const normalized = normalizePostalCode(value);
  const usMatch = normalized.match(US_ZIP_RE);
  return usMatch?.[1] ?? normalized;
}

/** True for a 5-digit US ZIP (with optional +4 suffix). */
export function isUsZip(value: string): boolean {
  return US_ZIP_RE.test(normalizePostalCode(value));
}

/** True for a Canadian postal code in either "K1A0B1" or "K1A 0B1" form. */
export function isCaPostal(value: string): boolean {
  return CA_POSTAL_RE.test(normalizePostalCode(value));
}

/** True for a Canadian FSA such as "M5V". */
export function isCaFsa(value: string): boolean {
  return CA_FSA_RE.test(normalizePostalCode(value));
}

/** True for any supported postal-code shape (US or CA). */
export function isValidPostalCode(value: string): boolean {
  return isUsZip(value) || isCaPostal(value);
}

/** Lookup keys for an entered ZIP/postal code. CA postal codes also match their FSA. */
export function postalAreaLookupCodes(value: string): string[] {
  const normalized = normalizePostalAreaCode(value);
  if (!normalized) return [];
  const codes = [normalized];
  if (isCaPostal(normalized)) codes.push(normalized.slice(0, 3));
  return [...new Set(codes)];
}

/**
 * Sanitize on-input. We intentionally do NOT strip non-digits any more —
 * Canadian codes use letters. Just upper-case and clamp length.
 */
export function sanitizePostalCodeInput(value: string): string {
  return value.toUpperCase().slice(0, POSTAL_CODE_MAX_LENGTH);
}
